import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Icon from '../comps/Icon';
import reservationsSvc from '../services/reservations';
import manageareas from '../services/manageareas';
import managetables from '../services/managetables';
import '../style/css/calendar2026.css';

// PHP liveStatus → the calendar's status buckets (STATUS keys). Keeps the existing
// design/labels; anything unknown falls back to 'confirmed'.
const LIVE_TO_CAL = {
  booked: 'confirmed', arrived: 'seated', seated: 'seated',
  'no-show': 'cancelled', cancelled: 'cancelled',
};
// The reverse, for the detail-popover actions that write back to the DB.
const CAL_TO_LIVE = { confirmed: 'booked', cancelled: 'cancelled', seated: 'seated' };

/*
 * ReservationCalendar — the reservations DIARY (יומן הזמנות) for the HOPPA
 * operator app. Four views: DAY / WEEK / MONTH / LIST (default WEEK).
 *
 * Operator intent (verbatim): "יומן תורים והזמנות לפי יום חודש שבוע או תצוגת רשימה".
 *
 * All data below is MOCK client state — clearly marked — shaped so it can later
 * wire to the planned reservation acts (reservationsList {from,to} etc.). No
 * backend calls here. This renders inside the authenticated app shell.
 */

/* ------------------------------------------------------------------ *
 *  MOCK DATA — replace with reservationsList {from,to}
 *  shape: {id, date(YYYY-MM-DD), time(HH:MM), name, party, phone,
 *          status('confirmed'|'pending'|'seated'|'cancelled'), area, table}
 * ------------------------------------------------------------------ */

// Anchor the mock data around "today" so the calendar always has something to
// show regardless of when it's opened. We spread reservations across the
// current week + a few into next/prev week.
const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const shiftDays = (base, n) => {
  const d = new Date(base);
  d.setDate(d.getDate() + n);
  return d;
};

/* ------------------------------------------------------------------ *
 *  Constants + helpers
 * ------------------------------------------------------------------ */

const VIEWS = [
  { id: 'day',   label: 'יום' },
  { id: 'week',  label: 'שבוע' },
  { id: 'month', label: 'חודש' },
  { id: 'list',  label: 'רשימה' },
];

// Hebrew day names, Sunday-first.
const HE_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const HE_DAYS_SHORT = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];
const HE_MONTHS = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];

// Day view time-grid rows: 12:00 → 24:00.
const DAY_START_HOUR = 12;
const DAY_END_HOUR = 24;
const DAY_HOURS = (() => {
  const out = [];
  for (let h = DAY_START_HOUR; h < DAY_END_HOUR; h += 1) out.push(h);
  return out;
})();
const ROW_HEIGHT = 56; // px per hour row (keeps blocks readable)

const STATUS = {
  confirmed: { label: 'מאושר',  cls: 'confirmed' },
  pending:   { label: 'ממתין',  cls: 'pending' },
  seated:    { label: 'ישוב',   cls: 'seated' },
  cancelled: { label: 'בוטל',   cls: 'cancelled' },
};

const toMin = (hhmm) => {
  if (!hhmm || !hhmm.includes(':')) return 0;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

// Start-of-day Date from a YYYY-MM-DD string (local).
const parseISO = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// Sunday that starts the week containing `d`.
const startOfWeek = (d) => {
  const out = new Date(d);
  out.setDate(out.getDate() - out.getDay());
  out.setHours(0, 0, 0, 0);
  return out;
};

/* ------------------------------------------------------------------ *
 *  Component
 * ------------------------------------------------------------------ */

const ReservationCalendar = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('week');
  // The "cursor" date — the period anchor for day/week/month.
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [selectedId, setSelectedId] = useState(null); // detail popover
  const [query, setQuery] = useState(''); // list-view search

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // The visible date window [start,end] to fetch from the DB, per view.
  const range = useMemo(() => {
    if (view === 'day') return { start: cursor, end: cursor };
    if (view === 'week' || view === 'list') {
      const s = startOfWeek(cursor);
      return { start: s, end: shiftDays(s, 6) };
    }
    // month: cover the full 6×7 grid (starts on the Sunday before the 1st).
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gs = startOfWeek(first);
    return { start: gs, end: shiftDays(gs, 41) };
  }, [view, cursor]);

  // Load real reservations for the window + area/table names, mapped to the calendar shape.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ rows }, areaList, tables] = await Promise.all([
        reservationsSvc.listRange({ startingDate: iso(range.start), endingDate: iso(range.end) }),
        manageareas.getAllArea(true).catch(() => []),
        managetables.getAllTables(true).catch(() => []),
      ]);
      const areaName = {};
      (Array.isArray(areaList) ? areaList : []).forEach((a) => { areaName[String(a.id || a.ID)] = a.name; });
      const tableName = {};
      (Array.isArray(tables) ? tables : []).forEach((t) => { tableName[String(t.id)] = t.name; });
      const mapped = (rows || []).map((r) => ({
        id: r.id,
        date: r.date,
        time: r.time ? String(r.time).slice(0, 5) : '',
        name: r.name || '—',
        party: r.party || '',
        phone: r.phone || '',
        status: LIVE_TO_CAL[r.status] || 'confirmed',
        area: areaName[String(r.area)] || (r.area != null && Number(r.area) ? `אזור ${r.area}` : ''),
        table: tableName[String(r.tableId)] || (r.tableId != null && Number(r.tableId) ? `שולחן ${r.tableId}` : '—'),
      }));
      setReservations(mapped);
    } catch (e) {
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  // Reservations indexed by date string for quick per-day lookup.
  const byDate = useMemo(() => {
    const map = {};
    reservations.forEach((r) => {
      (map[r.date] = map[r.date] || []).push(r);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => toMin(a.time) - toMin(b.time)));
    return map;
  }, [reservations]);

  const dayReservations = useCallback(
    (d) => (byDate[iso(d)] || []),
    [byDate]
  );

  /* ---- period navigation ---- */
  const step = useCallback(
    (dir) => {
      setCursor((cur) => {
        const d = new Date(cur);
        if (view === 'day') d.setDate(d.getDate() + dir);
        else if (view === 'week') d.setDate(d.getDate() + dir * 7);
        else if (view === 'month') {
          // Pin to the 1st before changing month so day-31 never rolls the
          // month over (e.g. Jan 31 → +1 would land in March).
          d.setDate(1);
          d.setMonth(d.getMonth() + dir);
        }
        else d.setDate(d.getDate() + dir * 7); // list steps by week
        return d;
      });
    },
    [view]
  );
  const goToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setCursor(d);
  };

  /* ---- period label ---- */
  const periodLabel = useMemo(() => {
    if (view === 'day') {
      return `יום ${HE_DAYS[cursor.getDay()]}, ${cursor.getDate()} ב${HE_MONTHS[cursor.getMonth()]}`;
    }
    if (view === 'week' || view === 'list') {
      const s = startOfWeek(cursor);
      const e = shiftDays(s, 6);
      if (s.getMonth() === e.getMonth()) {
        return `${s.getDate()}–${e.getDate()} ב${HE_MONTHS[s.getMonth()]} ${s.getFullYear()}`;
      }
      return `${s.getDate()} ב${HE_MONTHS[s.getMonth()]} – ${e.getDate()} ב${HE_MONTHS[e.getMonth()]} ${e.getFullYear()}`;
    }
    // month
    return `${HE_MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  }, [view, cursor]);

  /* ---- selected reservation (detail popover) ---- */
  const selected = useMemo(
    () => reservations.find((r) => r.id === selectedId) || null,
    [reservations, selectedId]
  );

  // Confirm/cancel from the detail popover — a REAL DB write (reservationSetStatus).
  // Optimistic locally, then re-sync from the server on failure.
  const setStatus = async (id, status) => {
    setReservations((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    const liveStatus = CAL_TO_LIVE[status] || 'booked';
    const res = await reservationsSvc.setStatus({ reservationId: id, status: liveStatus });
    if (!res || !res.ok) await load();
  };

  /* ---- open a specific day (from month cell click) ---- */
  const openDay = (d) => {
    const nd = new Date(d);
    nd.setHours(0, 0, 0, 0);
    setCursor(nd);
    setView('day');
  };

  /* ================================================================ *
   *  RENDER: DAY VIEW
   * ================================================================ */
  const renderDay = () => {
    const list = dayReservations(cursor);
    return (
      <div className="cal-day">
        {list.length === 0 && (
          <div className="cal-empty">
            <div className="cal-empty-emoji" aria-hidden="true"><Icon name="calendar" size={30} /></div>
            <p>אין הזמנות ביום זה</p>
          </div>
        )}
        <div className="cal-day-grid" style={{ ['--rowh']: `${ROW_HEIGHT}px` }}>
          {/* hour rulers */}
          <div className="cal-day-hours" aria-hidden="true">
            {DAY_HOURS.map((h) => (
              <div className="cal-day-hour" key={h}>
                <span className="cal-day-hour-lbl" dir="ltr">{String(h % 24).padStart(2, '0')}:00</span>
              </div>
            ))}
          </div>
          {/* reservation lane */}
          <div className="cal-day-lane">
            {DAY_HOURS.map((h) => (
              <div className="cal-day-slot" key={h} />
            ))}
            {list.map((r) => {
              const min = toMin(r.time);
              const top = ((min - DAY_START_HOUR * 60) / 60) * ROW_HEIGHT;
              const st = STATUS[r.status];
              return (
                <button
                  type="button"
                  key={r.id}
                  className={`cal-block status-${st.cls} ${selectedId === r.id ? 'is-open' : ''}`}
                  style={{ top: `${Math.max(top, 0)}px` }}
                  onClick={() => setSelectedId((cur) => (cur === r.id ? null : r.id))}
                >
                  <span className="cal-block-time" dir="ltr">{r.time}</span>
                  <span className="cal-block-name">{r.name}</span>
                  <span className="cal-block-meta"><Icon name="users" size={13} style={{ verticalAlign: 'middle' }} /> {r.party} · {st.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  /* ================================================================ *
   *  RENDER: WEEK VIEW
   * ================================================================ */
  const renderWeek = () => {
    const s = startOfWeek(cursor);
    const days = DAY_HOURS; // reuse hour set for the ruler
    const weekDays = Array.from({ length: 7 }, (_, i) => shiftDays(s, i));
    return (
      <div className="cal-week">
        {/* Desktop / tablet: 7-column time grid. RTL so ראשון sits on the right. */}
        <div className="cal-week-grid">
          <div className="cal-week-corner" aria-hidden="true" />
          {weekDays.map((d) => {
            const isToday = sameDay(d, today);
            return (
              <div key={iso(d)} className={`cal-week-head ${isToday ? 'is-today' : ''}`}>
                <span className="cal-week-dow">{HE_DAYS_SHORT[d.getDay()]}</span>
                <span className="cal-week-num">{d.getDate()}</span>
              </div>
            );
          })}

          {/* hour ruler column */}
          <div className="cal-week-hours" style={{ ['--rowh']: `${ROW_HEIGHT}px` }} aria-hidden="true">
            {days.map((h) => (
              <div className="cal-week-hour" key={h}>
                <span dir="ltr">{String(h % 24).padStart(2, '0')}:00</span>
              </div>
            ))}
          </div>

          {/* 7 day columns */}
          {weekDays.map((d) => {
            const list = dayReservations(d).filter((r) => r.status !== 'cancelled');
            return (
              <div key={iso(d)} className="cal-week-col" style={{ ['--rowh']: `${ROW_HEIGHT}px` }}>
                {days.map((h) => (
                  <div className="cal-week-cell" key={h} />
                ))}
                {list.map((r) => {
                  const min = toMin(r.time);
                  const top = ((min - DAY_START_HOUR * 60) / 60) * ROW_HEIGHT;
                  const st = STATUS[r.status];
                  return (
                    <button
                      type="button"
                      key={r.id}
                      className={`cal-chip status-${st.cls} ${selectedId === r.id ? 'is-open' : ''}`}
                      style={{ top: `${Math.max(top, 0)}px` }}
                      onClick={() => setSelectedId((cur) => (cur === r.id ? null : r.id))}
                      title={`${r.time} · ${r.name} · ${r.party}`}
                    >
                      <span dir="ltr" className="cal-chip-time">{r.time}</span>
                      <span className="cal-chip-name">{r.name}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Mobile: stacked per-day lists (no 7-col grid → no overflow at 390px). */}
        <div className="cal-week-stack">
          {weekDays.map((d) => {
            const list = dayReservations(d);
            const isToday = sameDay(d, today);
            return (
              <section key={iso(d)} className={`cal-stack-day ${isToday ? 'is-today' : ''}`}>
                <header className="cal-stack-head">
                  <button type="button" className="cal-stack-title" onClick={() => openDay(d)}>
                    <span className="cal-stack-dow">{HE_DAYS[d.getDay()]}</span>
                    <span className="cal-stack-date">{d.getDate()} ב{HE_MONTHS[d.getMonth()]}</span>
                    {isToday && <span className="cal-today-tag">היום</span>}
                  </button>
                  <span className="cal-stack-count">{list.length ? `${list.length} הזמנות` : '—'}</span>
                </header>
                {list.length === 0 ? (
                  <p className="cal-stack-empty">אין הזמנות</p>
                ) : (
                  <ul className="cal-stack-list">
                    {list.map((r) => {
                      const st = STATUS[r.status];
                      return (
                        <li key={r.id}>
                          <button
                            type="button"
                            className={`cal-row status-${st.cls}`}
                            onClick={() => setSelectedId(r.id)}
                          >
                            <span className="cal-row-time" dir="ltr">{r.time}</span>
                            <span className="cal-row-name">{r.name}</span>
                            <span className="cal-row-party"><Icon name="users" size={13} style={{ verticalAlign: 'middle' }} /> {r.party}</span>
                            <span className={`cal-badge badge-${st.cls}`}>{st.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      </div>
    );
  };

  /* ================================================================ *
   *  RENDER: MONTH VIEW
   * ================================================================ */
  const renderMonth = () => {
    const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = startOfWeek(firstOfMonth);
    // 6 rows × 7 = 42 cells covers any month.
    const cells = Array.from({ length: 42 }, (_, i) => shiftDays(gridStart, i));
    return (
      <div className="cal-month">
        <div className="cal-month-dow">
          {HE_DAYS_SHORT.map((d) => (
            <div className="cal-month-dow-cell" key={d}>{d}</div>
          ))}
        </div>
        <div className="cal-month-grid">
          {cells.map((d) => {
            const inMonth = d.getMonth() === cursor.getMonth();
            const isToday = sameDay(d, today);
            const list = dayReservations(d).filter((r) => r.status !== 'cancelled');
            const shown = list.slice(0, 3);
            const extra = list.length - shown.length;
            return (
              <button
                type="button"
                key={iso(d)}
                className={`cal-month-cell ${inMonth ? '' : 'is-muted'} ${isToday ? 'is-today' : ''}`}
                onClick={() => openDay(d)}
              >
                <span className="cal-month-num">{d.getDate()}</span>
                <span className="cal-month-pills">
                  {shown.map((r) => {
                    const st = STATUS[r.status];
                    return (
                      <span key={r.id} className={`cal-pill status-${st.cls}`}>
                        <span dir="ltr" className="cal-pill-time">{r.time}</span>
                        <span className="cal-pill-name">{r.name}</span>
                      </span>
                    );
                  })}
                  {extra > 0 && <span className="cal-pill-more">＋{extra} נוספות</span>}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /* ================================================================ *
   *  RENDER: LIST VIEW
   * ================================================================ */
  const renderList = () => {
    const q = query.trim();
    // Upcoming (from today onward), chronological, grouped by date.
    const upcoming = reservations
      .filter((r) => parseISO(r.date) >= today)
      .filter((r) => (q ? r.name.includes(q) : true))
      .sort((a, b) => (a.date === b.date ? toMin(a.time) - toMin(b.time) : a.date < b.date ? -1 : 1));

    const groups = [];
    upcoming.forEach((r) => {
      const last = groups[groups.length - 1];
      if (last && last.date === r.date) last.items.push(r);
      else groups.push({ date: r.date, items: [r] });
    });

    return (
      <div className="cal-list">
        <div className="cal-list-toolbar">
          <input
            className="cal-search"
            type="search"
            placeholder="חיפוש לפי שם…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="חיפוש הזמנה לפי שם"
          />
        </div>

        {groups.length === 0 ? (
          <div className="cal-empty">
            <div className="cal-empty-emoji" aria-hidden="true"><Icon name="search" size={30} /></div>
            <p>{q ? `לא נמצאו הזמנות עבור "${q}"` : 'אין הזמנות קרובות'}</p>
          </div>
        ) : (
          groups.map((g) => {
            const gd = parseISO(g.date);
            const isToday = sameDay(gd, today);
            return (
              <section className="cal-list-group" key={g.date}>
                <h3 className="cal-list-date">
                  <span className="cal-list-dow">{HE_DAYS[gd.getDay()]}</span>
                  <span>{gd.getDate()} ב{HE_MONTHS[gd.getMonth()]}</span>
                  {isToday && <span className="cal-today-tag">היום</span>}
                  <span className="cal-list-count">{g.items.length}</span>
                </h3>

                {/* Desktop table */}
                <table className="cal-table">
                  <thead>
                    <tr>
                      <th>שעה</th>
                      <th>שם</th>
                      <th>סועדים</th>
                      <th>אזור</th>
                      <th>שולחן</th>
                      <th>סטטוס</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((r) => {
                      const st = STATUS[r.status];
                      return (
                        <tr
                          key={r.id}
                          className={`status-${st.cls}`}
                          onClick={() => setSelectedId(r.id)}
                        >
                          <td dir="ltr" className="cal-td-time">{r.time}</td>
                          <td className="cal-td-name">{r.name}</td>
                          <td><Icon name="users" size={13} style={{ verticalAlign: 'middle' }} /> {r.party}</td>
                          <td>{r.area}</td>
                          <td>{r.table}</td>
                          <td><span className={`cal-badge badge-${st.cls}`}>{st.label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Mobile cards */}
                <ul className="cal-cards">
                  {g.items.map((r) => {
                    const st = STATUS[r.status];
                    return (
                      <li key={r.id}>
                        <button
                          type="button"
                          className={`cal-card status-${st.cls}`}
                          onClick={() => setSelectedId(r.id)}
                        >
                          <div className="cal-card-top">
                            <span className="cal-card-time" dir="ltr">{r.time}</span>
                            <span className={`cal-badge badge-${st.cls}`}>{st.label}</span>
                          </div>
                          <div className="cal-card-name">{r.name}</div>
                          <div className="cal-card-meta">
                            <span><Icon name="users" size={13} style={{ verticalAlign: 'middle' }} /> {r.party}</span>
                            <span>{r.area}</span>
                            <span className="cal-card-table">שולחן {r.table}</span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })
        )}
      </div>
    );
  };

  /* ================================================================ */

  return (
    <div className="cal-page">
      {/* ---------------- Header ---------------- */}
      <header className="cal-header">
        <div className="cal-header-top">
          <h1 className="cal-title">יומן הזמנות</h1>
          <div className="cal-switcher" role="tablist" aria-label="בחירת תצוגה">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                role="tab"
                aria-selected={view === v.id}
                className={`cal-seg ${view === v.id ? 'is-active' : ''}`}
                onClick={() => { setView(v.id); setSelectedId(null); }}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        <div className="cal-nav">
          <div className="cal-nav-btns">
            {/* In RTL: "prev" (earlier) is the button pointing right (›? no) — we
                label them semantically and let arrows read naturally. */}
            <button type="button" className="cal-nav-btn" onClick={() => step(-1)} aria-label="תקופה קודמת">›</button>
            <button type="button" className="cal-today-btn" onClick={goToday}>היום</button>
            <button type="button" className="cal-nav-btn" onClick={() => step(1)} aria-label="תקופה הבאה">‹</button>
          </div>
          <span className="cal-period">
            {periodLabel}
            {loading && <span style={{ marginInlineStart: 8, fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', fontWeight: 'var(--fw-medium)' }}>· טוען…</span>}
          </span>
        </div>

        <div className="cal-legend" aria-label="מקרא סטטוסים">
          <span className="cal-legend-item"><i className="dot dot-confirmed" />מאושר</span>
          <span className="cal-legend-item"><i className="dot dot-pending" />ממתין</span>
          <span className="cal-legend-item"><i className="dot dot-seated" />ישוב</span>
          <span className="cal-legend-item"><i className="dot dot-cancelled" />בוטל</span>
        </div>
      </header>

      {/* ---------------- View body ---------------- */}
      <div className="cal-body">
        {view === 'day' && renderDay()}
        {view === 'week' && renderWeek()}
        {view === 'month' && renderMonth()}
        {view === 'list' && renderList()}
      </div>

      {/* ---------------- Detail popover ---------------- */}
      {selected && (
        <>
          <div className="cal-scrim" onClick={() => setSelectedId(null)} />
          <div className="cal-detail" role="dialog" aria-label={`פרטי הזמנה — ${selected.name}`}>
            <div className="cal-detail-head">
              <div>
                <h2 className="cal-detail-name">{selected.name}</h2>
                <span className={`cal-badge badge-${STATUS[selected.status].cls}`}>
                  {STATUS[selected.status].label}
                </span>
              </div>
              <button type="button" className="cal-detail-close" onClick={() => setSelectedId(null)} aria-label="סגירה">✕</button>
            </div>

            <dl className="cal-detail-grid">
              <div><dt>שעה ותאריך</dt><dd dir="ltr">{selected.time} · {selected.date}</dd></div>
              <div><dt>סועדים</dt><dd><Icon name="users" size={14} style={{ verticalAlign: 'middle' }} /> {selected.party}</dd></div>
              <div><dt>טלפון</dt><dd dir="ltr">{selected.phone}</dd></div>
              <div><dt>אזור</dt><dd>{selected.area}</dd></div>
              <div><dt>שולחן</dt><dd>{selected.table}</dd></div>
            </dl>

            <div className="cal-detail-actions">
              <button
                type="button"
                className="cal-act cal-act-confirm"
                onClick={() => setStatus(selected.id, 'confirmed')}
                disabled={selected.status === 'confirmed'}
              >
                אישור
              </button>
              <button
                type="button"
                className="cal-act cal-act-cancel"
                onClick={() => setStatus(selected.id, 'cancelled')}
                disabled={selected.status === 'cancelled'}
              >
                ביטול
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReservationCalendar;
